import type {
  CircleLayerSpecification,
  FillLayerSpecification,
  HeatmapLayerSpecification,
  LineLayerSpecification,
  RasterSourceSpecification,
  SymbolLayerSpecification,
  VectorSourceSpecification,
} from 'maplibre-gl'
import type { LegendIconTypes } from '../../../../components/regionen/pageRegionSlug/SidebarLayerControls/Legend/LegendIcons/types'
import type { MapDataCategoryId } from './mapDataCategories/MapDataCategoryId'
import type { SourcesId } from './mapDataSources/sources.const'
import type { TableId } from './mapDataSources/tables.const'
import type { StyleId, SubcategoryId } from './typeId'

/** @desc: The background tiles, configured in 'sourcesBackgroundsRaster.const.ts' */
export type MapDataBackgroundSource<TIds> = {
  id: TIds
  name: string
  /** @desc URL of the raster tiles */
  tilesUrl: string
  attributionHtml: string
  /** @desc Show link to the external legend of that map layer. Will replace {z}/{x}/{y} if present  */
  legendUrl?: string
  maxzoom?: RasterSourceSpecification['maxzoom']
  minzoom?: RasterSourceSpecification['minzoom']
  tileSize?: RasterSourceSpecification['tileSize']
  scheme?: RasterSourceSpecification['scheme']
  /** @desc `photo` marks an aerial image; the Messen mode picks its background from those. */
  category?:
    | 'photo'
    | 'map'
    | 'historicmap'
    | 'osmbasedmap'
    | 'historicphoto'
    | 'qa'
    | 'elevation'
    | 'other'
  /** @desc The Editor Layer Index recommends this layer for its area. */
  best?: boolean
  /** @desc When the image was taken, e.g. `2025` or `2025-04` (Editor Layer Index `end_date`). */
  endDate?: string
  /** @desc Coverage as `[west, south, east, north]`; missing for worldwide layers. */
  bbox?: [number, number, number, number]
}

export type MapDataSourceInspectorEditor = {
  name: string
  /** @desc `properties[idKey]` will replace `{editor_id}` in the URL */
  idKey?: string
  /** @desc Allowed replacements are `{zoom}`, `{latitude}`, `{longitude}`, `{short_osm_type}`, `{long_osm_type}`, `{editor_id}`, `{osm_id}` */
  urlTemplate: `https://${string}`
}

type MapDataSourceInspector =
  | {
      enabled: true
      /** @desc The key used by the highlighting LayerHighlight component to change the appearance of the selected element */
      highlightingKey: string
      /** @desc A sorted list of keys that we officially document.
       * Keys of type `composit_*` require their own TableRowCell-Component.
       * Keys of type `*__if_present` are only presented if a value is present.
       * (Keys that are not mentioned here are for debugging only.) */
      documentedKeys?: (string | `composit_${string}` | `${string}__if_present`)[]
      editors?: MapDataSourceInspectorEditor[]
    }
  | {
      enabled: false
    }

export type MapDataOsmIdConfig =
  | undefined
  | { osmType: string; osmId: string }
  | { osmTypeId: string }

type MapDataSourceShared<TIds> = {
  id: TIds
  /** @desc minzoom:4 (default, see `SIMPLIFY_MIN_ZOOM`) means no data is loaded for 0-4, only from 5+ (zoomed in) data is present
   * @desc `0---4=minzoom->-----maxzoom=14=overzoom->---22` */
  minzoom: VectorSourceSpecification['minzoom']
  /** @desc maxzoom:14 (default, see `SIMPLIFY_MAX_ZOOM`) means data is still visible for 14+ but it uses the data from z=14 (overzoom)
   * @desc `0---4=minzoom->-----maxzoom=14=overzoom->---22` */
  maxzoom: VectorSourceSpecification['maxzoom']
  attributionHtml: string
  licence: 'ODbL' | undefined
  promoteId: VectorSourceSpecification['promoteId'] | undefined
  osmIdConfig: MapDataOsmIdConfig
  /** @desc Inspector: Enable and configure Inspector */
  inspector: MapDataSourceInspector
}

/** Processing tables; tile URL via `getMapDataSourceTilesUrl` (`atlas_generalized_*` PG functions). */
type ProcessingMapDataSource<TIds> = MapDataSourceShared<TIds> & {
  tileTables: readonly TableId[]
}

/** Explicit tile URL (internal Martin, Lars, Mapillary, …). */
export type ExplicitTilesUrlMapDataSource<TIds> = MapDataSourceShared<TIds> & {
  tileTables: null
  /** @desc URL of the vector tiles */
  tilesUrl: string
}

/** @desc: Our own vector tile layers configured in 'sources.const.ts' */
export type MapDataSource<TIds> =
  | ProcessingMapDataSource<TIds>
  | ExplicitTilesUrlMapDataSource<TIds>

export const hasExplicitTilesUrl = <TIds>(
  source: MapDataSource<TIds>,
): source is ExplicitTilesUrlMapDataSource<TIds> => source.tileTables === null

export type StaticMapDataCategory = {
  id: MapDataCategoryId
  name: string
  desc?: string
  subcategories: StaticMapDataSubcategory[]
  /** @desc Set of subcategory IDs after which a visual spacer should be rendered */
  spacerAfter?: Set<SubcategoryId>
}

type StaticMapDataSubcategory = FileMapDataSubcategory & {
  id: SubcategoryId
  defaultStyle: 'default' | 'hidden'
  // The order on the map is independent of the order in the UI, see /admin/layer-order.
}

// We have some layer without content in our Maptiler basemap style that can be used as an anchor.
// Listed from the bottom of the map to the top.
// Modify at https://cloud.maptiler.com/maps/editor?map=08357855-50d4-44e1-ac9f-ea099d9de4a5
const APP_ANCHOR_IDS = [
  'atlas-app-beforeid-above-landuse',
  'atlas-app-beforeid-below-road',
  'atlas-app-beforeid-below-roadname',
  'atlas-app-beforeid-group2',
  'atlas-app-beforeid-fallback',
  'atlas-app-beforeid-group1',
  'atlas-app-beforeid-top',
] as const

type AppAnchorId = (typeof APP_ANCHOR_IDS)[number]

export type TBeforeIds =
  | 'housenumber'
  | 'boundary_country_outline'
  | 'boundary_country'
  | 'landuse'
  | 'building'
  | AppAnchorId
  | undefined

/** @desc: Thematic "filter" on the raw vector tile data; eg. 'Radinfrastruktur, Oberflächen, Beleuchtung' */
export type FileMapDataSubcategory = {
  id: SubcategoryId
  name: string
  sourceId: SourcesId
  /** @desc Insert layer before / above the given layer ID. The beforeId layer will come after. */
  beforeId?: TBeforeIds
} & (
  | {
      ui: 'dropdown'
      styles: (FileMapDataSubcategoryStyle | FileMapDataSubcategoryHiddenStyle)[]
    }
  | {
      ui: 'checkbox'
      styles: [FileMapDataSubcategoryStyle]
    }
)

/** @desc: Different visual views of the same thematic data; Can contain static filter, eg. "only lines with todos"); eg. 'Default,  Bad infrastructure (only)', 'Where debugging is needed' */
export type FileMapDataSubcategoryStyle = {
  id: StyleId
  name: string
  category?: string
  layers: FileMapDataSubcategoryStyleLayer[]
  legends?: null | FileMapDataSubcategoryStyleLegend[]
}

export type FileMapDataSubcategoryHiddenStyle = {
  id: 'hidden'
  name: string
  category?: never
  layers?: never
  legends?: never
}

/** Layer types used for TILDA layers and when filtering layers to render. Convention: same set in buildTildaLayerProps and RENDERABLE_LAYER_TYPES. All have a debug style in debugLayerStyles. */
export type RenderableLayerType = 'fill' | 'line' | 'circle' | 'symbol' | 'heatmap'

/** @desc: The technical glue between sources and styles. The name "layers" is defined by the library we use. */
export type FileMapDataSubcategoryStyleLayer = (
  | CircleLayerSpecification
  | FillLayerSpecification
  | HeatmapLayerSpecification
  | LineLayerSpecification
  | SymbolLayerSpecification
) & {
  'source-layer': string
  /** @desc Override default layer stacking when using the default background map. */
  beforeId?: TBeforeIds
  /**
   * @default `true`
   * @desc optional `false` will hide the layer from `interactiveLayerIds` */
  interactive?: false
}

/** @desc: Optional legend to explain a given layer */
export type FileMapDataSubcategoryStyleLegend = {
  id: string
  name: string
  /** @description Strings can be Markdown */
  desc?: string[]
  style:
    | {
        type: Exclude<LegendIconTypes, 'line'>
        color: string
        width?: never
        dasharray?: never
      }
    | {
        type: Extract<LegendIconTypes, 'line'>
        color: string
        width?: number
        dasharray?: number[]
      }
}
